'use client';

/**
 * Share-link transport — encrypt a bundle, upload it to the relay, and
 * hand the sender a short URL they can AirDrop / paste anywhere. The
 * decryption key lives in the URL fragment (which browsers never send to
 * servers), so the relay stores opaque ciphertext it cannot read.
 *
 * URL shape:
 *   https://app.knitterslibrary.com/s?c=<16-char code>#k=<base64url key>
 *
 * Privacy properties:
 *   - Relay sees code + ciphertext. Never the key.
 *   - Ciphertext is XChaCha20-Poly1305 with a random per-share 32-byte key.
 *   - Bundle auto-expires after 7 days (enforced server-side by KV TTL).
 *   - Upload is rate-limited per IP on the relay (20 / hour).
 */

import { encrypt, decrypt } from './crypto';

// Overridable via NEXT_PUBLIC_RELAY_HTTP_BASE so the containerized test
// runner can point at the in-cluster relay (same pattern as syncClient.ts
// and SyncPanel.tsx). Production deploys never set this env var so the
// canonical Cloudflare Worker URL stays the default.
const RELAY_HTTP_BASE =
  process.env.NEXT_PUBLIC_RELAY_HTTP_BASE ??
  'https://knitlib-sync.z3nlabs.workers.dev';

/** Canonical production origin. Used as the fallback when the app is
 *  rendered server-side or the current host isn't a known KL install. */
const PROD_ORIGIN = 'https://app.knitterslibrary.com';

/** Hosts where generating a share link should use the CURRENT origin rather
 *  than the canonical prod URL. dev.knitterslibrary.com is Sebastian's laptop
 *  proxied through the RPi; Veronika tests there. `localhost` covers the
 *  two-browser-profile dev testing workflow. For any other host we default
 *  to prod so an unknown embed or cache can't break the "open the PWA" story. */
const DEV_HOSTS = new Set(['dev.knitterslibrary.com', 'localhost', '127.0.0.1']);

function shareOrigin(): string {
  if (typeof window === 'undefined') return PROD_ORIGIN;
  const { hostname, origin } = window.location;
  if (hostname === 'app.knitterslibrary.com') return PROD_ORIGIN;
  if (DEV_HOSTS.has(hostname)) return origin; // use whatever URL the sender is on
  return PROD_ORIGIN;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(str: string): Uint8Array {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface ShareUploadResult {
  url: string;        // the full shareable URL
  code: string;       // the relay code (visible to sender for debugging)
  expiresInSeconds: number;
}

/** Encrypt `bundle`, upload the ciphertext to the relay, and return the
 *  shareable URL. Throws on network failure or rate limit. */
export async function encryptAndUploadShare(bundle: Blob): Promise<ShareUploadResult> {
  // Fresh 32-byte symmetric key per share. Never reused.
  const key = new Uint8Array(32);
  crypto.getRandomValues(key);

  const plaintext = new Uint8Array(await bundle.arrayBuffer());
  const ciphertext = encrypt(key, plaintext);

  const res = await fetch(`${RELAY_HTTP_BASE}/share/upload`, {
    method: 'POST',
    body: new Blob([ciphertext as BlobPart]),
    headers: { 'content-type': 'application/octet-stream' },
  });

  if (res.status === 429) {
    throw new ShareError('rate_limited', 'Too many shares from this device this hour. Try again later.');
  }
  if (res.status === 413) {
    throw new ShareError('too_large', 'Project is too large to share via link (max 5 MB).');
  }
  if (!res.ok) {
    throw new ShareError('upload_failed', `Relay returned ${res.status}`);
  }
  const data = (await res.json()) as { code: string; expiresInSeconds: number };

  const keyParam = bytesToBase64Url(key);
  const url = `${shareOrigin()}/s?c=${data.code}#k=${keyParam}`;
  return { url, code: data.code, expiresInSeconds: data.expiresInSeconds };
}

/** Fetch and decrypt a share. The recipient-side flow. Throws with a
 *  user-friendly `ShareError` on any problem. */
export async function fetchAndDecryptShare(code: string, keyBase64Url: string): Promise<Blob> {
  if (!/^[A-Za-z0-9]{16}$/.test(code)) {
    throw new ShareError('invalid_code', 'That share link looks malformed.');
  }
  let key: Uint8Array;
  try {
    key = base64UrlToBytes(keyBase64Url);
  } catch {
    throw new ShareError('invalid_key', 'That share link is missing its key.');
  }
  if (key.length !== 32) {
    throw new ShareError('invalid_key', 'That share link has a wrong-size key.');
  }

  const res = await fetch(`${RELAY_HTTP_BASE}/share/${code}`);
  if (res.status === 404) {
    throw new ShareError('expired', 'This share has expired or already been removed. Ask the sender for a fresh link.');
  }
  if (!res.ok) {
    throw new ShareError('fetch_failed', `Relay returned ${res.status}`);
  }
  const ciphertext = new Uint8Array(await res.arrayBuffer());

  let plaintext: Uint8Array;
  try {
    plaintext = decrypt(key, ciphertext);
  } catch {
    throw new ShareError('decrypt_failed', 'Could not decrypt this share — the link may be incomplete or corrupted.');
  }
  return new Blob([plaintext as BlobPart], { type: 'application/zip' });
}

/** Parse a share URL (full or relative) into its code + key. Returns null
 *  if the URL is not a recognisable share link. */
export function parseShareUrl(href: string): { code: string; keyBase64Url: string } | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  // Must be the share-receiver route exactly. `startsWith('/s')` would also
  // match /settings, /styleguide, /s/anything — a false positive now that
  // parseShareUrl is load-bearing for the in-app paste importer (build 1615).
  if (u.pathname !== '/s' && u.pathname !== '/s/') return null;
  const code = u.searchParams.get('c');
  if (!code) return null;
  const frag = u.hash.startsWith('#') ? u.hash.slice(1) : u.hash;
  const params = new URLSearchParams(frag);
  const key = params.get('k');
  if (!key) return null;
  return { code, keyBase64Url: key };
}

export class ShareError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'ShareError';
  }
}
